import { AttachmentService } from "../../services/attachment-service";
import { IEventAggregator, bindable, inject } from "aurelia";
import { getLogger } from "../../common/logging";
import { I18N } from "@aurelia/i18n";
import numeral from "numeral";
import { isHeic } from "../../common/heic";
import { formatName, formatsToSaveAs, joinWithOr } from "../../common/file-formats";

import { AttachmentChanged } from "../../messages/attachment-changed";
import { FieldInvalid } from "../../messages/field-invalid";

let log = getLogger("attachment");

@inject(Element, IEventAggregator, AttachmentService, I18N)
export class AttachmentV2CustomElement {
  @bindable referenceId;
  @bindable accept;
  @bindable maxFileSize;
  @bindable name;
  @bindable maxPages;
  @bindable description;
  @bindable hideDescriptionInput;
  @bindable index;
  @bindable type;
  @bindable editable;
  @bindable placeholder;
  @bindable enabled;
  @bindable allowPreview;
  @bindable inputId;

  constructor(element, ea, attachmentService, i18n) {
    this.element = element;
    this.ea = ea;
    this.attachmentService = attachmentService;
    this.i18n = i18n;

    this.uploadedAt = null;
    this.attachmentId = null;
    this.pages = null;
    this.type = "";
    this.progress = null;
    this.isUploading = false;
    this.hasAttachment = false;
    this.enabled = true;
    this.placeholder = "";
    this.isDragging = false;
    this.previewReady = false;
    // Sant bara medan förhandsgranskningsmodalen är öppen. Styr att PDF-iframen
    // rivs när modalen stängs – se showPdfPreview.
    this.previewOpen = false;
    if (this.description == null) this.description = "";
    if (!this.maxFileSize) this.maxFileSize = 20000000; //20MB
    if (!this.maxPages) this.maxPages = 3;
  }

  binding() {
    if (this.editable === undefined || this.editable === null) {
      this.editable = false;
    }
    if (this.editable === "true" || this.editable === true) {
      this.editable = true;
    } else {
      this.editable = false;
    }

    if (this.allowPreview === undefined || this.allowPreview === null) {
      this.allowPreview = true;
    }
    if (this.allowPreview === "false" || this.allowPreview === false) {
      this.allowPreview = false;
    } else {
      this.allowPreview = true;
    }
  }

  attached() {
    this.wireInputHandlers();

    this.attachmentService
      .get(this.referenceId, this.name)
      .then((attachment) => this.setAttachment(attachment))
      .catch((e) => log.error("failed fetching attachments:" + e.message));

    this.fieldInvalidSubscription = this.ea.subscribe(FieldInvalid, (m) => {
      if (m.fieldName === this.name) {
        this.dropzone.classList.add('has-error');
      }
    });
  }

  wireInputHandlers() {
    this.browse.addEventListener('change', (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = '';
      if (file) this.handleSelectedFile(file);
    });

    this.dropzone.addEventListener('dragenter', () => {
      if (!this.hasAttachment && this.enabled) {
        this.isDragging = true;
      }
    });

    this.dropzone.addEventListener('dragleave', (e) => {
      if (e.target === this.dropzone) {
        this.isDragging = false;
      }
    });

    this.dropzone.addEventListener('dragover', (e) => {
      e.preventDefault();
    });

    this.dropzone.addEventListener('drop', (e) => {
      e.preventDefault();
      this.isDragging = false;
      if (this.hasAttachment || !this.enabled) return;
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.handleSelectedFile(file);
    });
  }

  async handleSelectedFile(file) {
    this.error = "";
    // A HEIC image can't be printed, whatever its name says, so it is refused in every field.
    if (await isHeic(file).catch(() => false)) {
      this.error = this.i18n.tr("attachment.heicNotAccepted");
      return;
    }

    this.isAcceptableSize = this.isOfAcceptableSize(file.size);
    this.isAcceptableType = this.isOfAcceptableType(file.type, this.accept);

    if (!this.isAcceptableSize) {
      this.error = this.i18n.tr("attachment.fileToLarge", {
        size: numeral(file.size).format("0.0 b"),
        maxSize: numeral(this.maxFileSize).format("0.0 b"),
      });
      return;
    }
    if (!this.isAcceptableType) {
      this.error = this.i18n.tr("attachmentV2.wrongFileType", {
        accepted: this.getAcceptedFileTypes(),
        interpolation: { escapeValue: false },
      });
      return;
    }

    if (!this.type) {
      this.type = this.getType(file.type);
    }

    this.isUploading = true;
    this.uploadViaPresignedUrl(file);
  }

  descriptionChanged() {
    if (!this.hasAttachment) return;
    this.attachmentService.save(this.attachmentId, this.description);
  }

  setAttachment(attachment) {
    if (attachment === null) return;
    this.hasAttachment = true;
    this.attachmentId = attachment.id;
    this.attachmentUrl = this.attachmentService.getAttachmentUrl(
      this.attachmentId
    );
    this.uploadedAt = attachment.uploaded;
    this.pages = attachment.pages;
    this.fileSize = attachment.length;
    this.originalName = attachment.original_name;
    this.description = attachment.description;
    this.type = attachment.type || this.getType(attachment.content_type || '');
    this.previewReady = true;
    this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, true));
    this.removeErrorClass();
  }

  clearAttachment() {
    this.hasAttachment = false;
    this.attachmentUrl = null;
    this.attachmentId = null;
    this.uploadedAt = null;
    this.pages = null;
    this.fileSize = null;
    this.previewReady = false;
    this.previewOpen = false;
    this.type = "";
    this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, false));
  }

  detaching() {
    if (this.fieldInvalidSubscription) {
      this.fieldInvalidSubscription.dispose();
    }
  }

  async uploadViaPresignedUrl(file) {
    const fileType = this.type || this.getType(file.type);

    try {
      if (this.attachmentId) {
        await this.attachmentService.remove(this.attachmentId);
      }

      const presignResult = await this.attachmentService.presign(
        file.name,
        this.name,
        this.referenceId,
        file.type,
        this.maxFileSize
      );

      await this.attachmentService.uploadToPresignedPost(
        presignResult.url,
        presignResult.fields,
        file,
        (progress) => { this.progress = progress; }
      );

      const confirmResult = await this.attachmentService.confirm(
        presignResult.tempKey,
        this.name,
        file.name,
        this.referenceId,
        this.description,
        this.index,
        fileType,
        this.isImageOnly ? 0 : this.maxPages
      );

      this.isUploading = false;

      if (confirmResult.success) {
        this.hasAttachment = true;
        this.uploadedAt = new Date().toISOString();
        this.attachmentId = confirmResult.id;
        this.pages = confirmResult.pages;
        this.contentType = file.type;
        this.type = fileType;
        this.fileSize = file.size;
        this.originalName = file.name;

        this.removeErrorClass();

        this.attachmentUrl = this.attachmentService.getAttachmentUrl(this.attachmentId);
        this.previewReady = true;

        this.ea.publish(new AttachmentChanged(this.attachmentId, this.name, true));
        this.moveFocusTo(this.removeButton);
      } else {
        this.clearAttachment();
        if (confirmResult.error === "pages") {
          this.error = this.i18n.tr("attachment.tooManyPages", {
            pages: confirmResult.pages,
            maxPages: this.maxPages,
          });
        } else if (confirmResult.error === "format") {
          this.error = this.i18n.tr("attachment.unreadableFile", {
            accepted: formatsToSaveAs(this.accept, this.i18n.tr("attachmentV2.or")),
            interpolation: { escapeValue: false },
          });
        } else {
          this.error = this.i18n.tr("attachment.unknownError");
        }
      }
    } catch (e) {
      log.error("Presigned upload failed:", e);
      this.isUploading = false;
      this.clearAttachment();

      if (e.statusCode && e.response) {
        try {
          const body = JSON.parse(e.response);
          if (Array.isArray(body) && body.length > 0 && body[0].type === "invalid-pdf") {
            this.error = this.i18n.tr("attachment.invalidpdf", { msg: body[0].message });
          } else {
            this.error = this.i18n.tr("attachment.unknownError");
          }
        } catch (_) {
          this.error = this.i18n.tr("attachment.unknownError");
        }
      } else {
        this.error = this.i18n.tr("attachment.unknownError");
      }
    }
  }

  isOfAcceptableSize(size) {
    return !this.maxFileSize || size < this.maxFileSize;
  }

  isOfAcceptableType(fileType, types) {
    return types
      .split(",")
      .some(
        (type) =>
          type === fileType ||
          (type.split("/")[0] === fileType.split("/")[0] &&
            type.split("/")[1] === "*")
      );
  }

  remove() {
    if (this.attachmentId) {
      this.attachmentService.remove(this.attachmentId);
      this.clearAttachment();
      this.moveFocusTo(this.browse);
    }
  }

  handleIconClick() {
    if (this.hasAttachment && this.allowPreview && this.previewReady) {
      this.openAttachment();
    } else if (!this.hasAttachment && this.enabled) {
      this.browse.click();
    }
  }

  openAttachment() {
    if (this.hasAttachment && this.attachmentUrl && this.allowPreview && this.previewReady) {
      const modalId = `filePreviewModal-${this.name}`;
      const modalElement = document.getElementById(modalId);
      if (modalElement) {
        this.previewOpen = true;
        $(modalElement)
          .one('hidden.bs.modal', () => {
            this.previewOpen = false;
          })
          .modal('show');
      }
    } else if (this.hasAttachment && !this.previewReady) {
      log.info('Preview not ready yet, please wait a moment...');
    }
  }

  // PDF-förhandsgranskningen renderas av Chromes inbyggda PDF-visare, som är en
  // komponentextension. Ligger iframen kvar i DOM efter att modalen stängts kan
  // DevTools blockeras för sidan, så den existerar bara medan modalen är öppen.
  get showPdfPreview() {
    return this.previewOpen && !!this.attachmentUrl && this.type !== 'Image';
  }

  // Knappen som användes döljs när en fil laddats upp eller tagits bort. Fokus flyttas till den som
  // tar dess plats, annars hamnar det på sidans början (WCAG 2.4.3). Visningen ändras först i nästa task.
  // Har användaren gått vidare till ett annat fält medan filen laddades upp får fokus stanna där.
  moveFocusTo(element) {
    setTimeout(() => {
      const active = document.activeElement;
      const lost = !active || active === document.body || active === this.browse || active === this.removeButton;
      if (lost) element?.focus();
    });
  }

  getType(t) {
    if (t.toLowerCase().startsWith("image")) return "Image";
    if (t.toLowerCase().endsWith("pdf")) return "Document";
    return "Other";
  }

  getAcceptedFileTypes() {
    if (!this.accept) return "";

    const types = this.accept.split(",").map(type => type.trim());
    const readable = types.map(type => type === "image/*" ? this.i18n.tr("attachmentV2.images") : formatName(type));
    return joinWithOr(readable, this.i18n.tr("attachmentV2.or"));
  }

  get isImageOnly() {
    const types = (this.accept || "")
      .split(",")
      .map((t) => t.trim())
      .filter((t) => t);
    if (types.length === 0) return false;
    return types.every((t) => t.startsWith("image/"));
  }

  get showPageLimit() {
    if (this.isImageOnly) return false;
    return !!this.maxPages;
  }

  get pageText() {
    if (!this.pages) return '';
    return this.pages == 1
      ? this.i18n.tr('attachmentV2.page')
      : this.i18n.tr('attachmentV2.pages');
  }

  get maxPagesText() {
    if (!this.maxPages) return '';
    return this.maxPages == 1
      ? this.i18n.tr('attachmentV2.page')
      : this.i18n.tr('attachmentV2.pages');
  }

  get iconTooltip() {
    if (this.hasAttachment && this.allowPreview && this.previewReady) {
      return this.i18n.tr('attachmentV2.clickToOpen');
    } else if (this.hasAttachment && this.allowPreview && !this.previewReady) {
      return this.i18n.tr('attachmentV2.pleaseWait');
    } else if (!this.hasAttachment && this.enabled) {
      return this.i18n.tr('attachmentV2.clickToSelect');
    }
    return '';
  }

  removeErrorClass() {
    if (this.dropzone) {
      this.dropzone.classList.remove('has-error');
    }
  }
}
